import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchConfirmationDetails, recordConfirmation, recordJustification } from '../services/agreementAccess';
import type { AccessPromptFormValues } from '../schemas/accessPrompt.schema';
import type { AccessStatus } from '../types/agreementAccess.types';
import { isConfirmationAlreadyRecorded } from '../utils/callerConfirmation';
import { agreementKeys } from './agreementKeys';

/** Outside agreementKeys.all on purpose: every details request writes an access record. */
export const callerConfirmationKey = (agreementId: string) => ['agreement-caller-confirmation', agreementId] as const;

export function useCallerConfirmationDetails(agreementId: string, enabled: boolean) {
    const queryClient = useQueryClient();

    // A mounted observer outlives gcTime, so the details are dropped when the prompt closes.
    useEffect(() => {
        if (enabled) return;
        queryClient.removeQueries({ queryKey: callerConfirmationKey(agreementId) });
    }, [enabled, agreementId, queryClient]);

    const details = useQuery({
        queryKey: callerConfirmationKey(agreementId),
        queryFn: () => fetchConfirmationDetails(agreementId),
        enabled,
        staleTime: Infinity,
        gcTime: 0,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    // Another tab confirmed first: the access status is re-read once for each such answer, and followed.
    const isAlreadyConfirmed = isConfirmationAlreadyRecorded(details.error);
    const { errorUpdatedAt } = details;
    const [recheckedErrorAt, setRecheckedErrorAt] = useState(0);
    useEffect(() => {
        if (!isAlreadyConfirmed) return;

        let isCurrent = true;
        void queryClient.invalidateQueries({ queryKey: agreementKeys.access(agreementId) }).finally(() => {
            if (isCurrent) setRecheckedErrorAt(errorUpdatedAt);
        });

        return () => {
            isCurrent = false;
        };
    }, [isAlreadyConfirmed, errorUpdatedAt, agreementId, queryClient]);

    return { ...details, isAwaitingAccessRecheck: isAlreadyConfirmed && recheckedErrorAt !== errorUpdatedAt };
}

export function useRecordAccessMutation(agreementId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (values: AccessPromptFormValues) => {
            const justification = await recordJustification(agreementId, values);
            const result = await recordConfirmation(agreementId, values);

            return { justification, result };
        },
        // The details query is left alone: removing it here would re-read, and record, a screen being left.
        onSuccess: ({ justification, result }) => {
            const passed = result.outcome === 'passed';

            queryClient.setQueryData<AccessStatus>(agreementKeys.access(agreementId), {
                required: true,
                justification: passed ? justification : null,
                confirmed: passed,
            });
            if (passed) void queryClient.invalidateQueries({ queryKey: agreementKeys.all });
        },
        // Awaited, so the mutation stays pending and the form unsubmittable until the status is re-read.
        onError: async (error) => {
            if (!isConfirmationAlreadyRecorded(error)) return;
            await queryClient.invalidateQueries({ queryKey: agreementKeys.access(agreementId) });
        },
    });
}
